import ReelsFeed from "@/components/ReelsFeed";
import { useSEO } from "@/hooks/useSEO";
import { Eye, EyeOff, Lock, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { BRAND_SITE_URL } from "@/lib/brandConfig";
import { useSearchParams } from "react-router-dom";

const GRANT_KEY = "private_reels_grant_v1";

type SessionGrant = {
  grant: string;
  exp: number;
  playbackIds: string[];
  maxViewsPerSession: number;
};

function readGrant(): SessionGrant | null {
  try {
    const raw = sessionStorage.getItem(GRANT_KEY);
    if (!raw) return null;
    const g = JSON.parse(raw) as SessionGrant;
    if (!g?.grant || !g.exp || g.exp * 1000 < Date.now()) {
      sessionStorage.removeItem(GRANT_KEY);
      return null;
    }
    return g;
  } catch {
    return null;
  }
}

function writeGrant(g: SessionGrant) {
  sessionStorage.setItem(GRANT_KEY, JSON.stringify(g));
}

/**
 * Private /reels — password or expirable share link.
 * Videos: brand creator_id + external_id=feed (or curated list from admin).
 */
const Reels = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState<SessionGrant | null>(() => readGrant());
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isShaking, setIsShaking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [accessMeta, setAccessMeta] = useState<{
    enabled: boolean;
    hasPassword: boolean;
    accessMode: string;
  } | null>(null);

  useSEO({
    title: "Reels Privados",
    description: "Contenido privado",
    keywords: "privado",
    ogImage: `${BRAND_SITE_URL}/og-reels.jpg`,
    ogTitle: "Reels Privados",
    ogDescription: "Contenido privado",
    type: "video.other",
    robots: "noindex, nofollow",
  });

  // Bootstrap: consume ?access= token or load public meta
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const access = searchParams.get("access");
      if (access) {
        setBusy(true);
        try {
          const res = await fetch("/api/private-reels/consume-link", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: access }),
          });
          const data = (await res.json()) as {
            ok?: boolean;
            grant?: string;
            exp?: number;
            playbackIds?: string[];
            maxViewsPerSession?: number;
            error?: string;
          };
          if (res.ok && data.ok && data.grant) {
            const g: SessionGrant = {
              grant: data.grant,
              exp: data.exp || Math.floor(Date.now() / 1000) + 3600,
              playbackIds: data.playbackIds || [],
              maxViewsPerSession: data.maxViewsPerSession || 0,
            };
            writeGrant(g);
            if (!cancelled) setSession(g);
            // strip token from URL
            const next = new URLSearchParams(searchParams);
            next.delete("access");
            setSearchParams(next, { replace: true });
          } else if (!cancelled) {
            setError(data.error || "Link inválido o expirado");
          }
        } catch {
          if (!cancelled) setError("No se pudo validar el link");
        } finally {
          if (!cancelled) {
            setBusy(false);
            setBootstrapping(false);
          }
        }
        return;
      }

      // existing grant?
      const existing = readGrant();
      if (existing) {
        if (!cancelled) {
          setSession(existing);
          setBootstrapping(false);
        }
        return;
      }

      try {
        const res = await fetch("/api/private-reels/config");
        const data = (await res.json()) as {
          enabled?: boolean;
          hasPassword?: boolean;
          accessMode?: string;
        };
        if (!cancelled) {
          setAccessMeta({
            enabled: data.enabled !== false,
            hasPassword: data.hasPassword !== false,
            accessMode: data.accessMode || "password_or_link",
          });
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount / access param
  }, []);

  useEffect(() => {
    if (!session) return;
    const ph = (
      window as Window & {
        posthog?: { stopSessionRecording?: () => void; startSessionRecording?: () => void };
      }
    ).posthog;
    ph?.stopSessionRecording?.();
    return () => {
      ph?.startSessionRecording?.();
    };
  }, [session]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = "";
    };
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setBusy(true);
      setError("");
      try {
        const res = await fetch("/api/private-reels/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        });
        const data = (await res.json()) as {
          ok?: boolean;
          grant?: string;
          exp?: number;
          playbackIds?: string[];
          maxViewsPerSession?: number;
          error?: string;
        };
        if (res.ok && data.ok && data.grant) {
          const g: SessionGrant = {
            grant: data.grant,
            exp: data.exp || Math.floor(Date.now() / 1000) + 3600,
            playbackIds: data.playbackIds || [],
            maxViewsPerSession: data.maxViewsPerSession || 0,
          };
          writeGrant(g);
          setSession(g);
          setPassword("");
        } else {
          setError(data.error || "Contraseña incorrecta");
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 500);
          setPassword("");
        }
      } catch {
        setError("Error de red");
      } finally {
        setBusy(false);
      }
    },
    [password],
  );

  if (bootstrapping) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white/60">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!session) {
    const linkOnly = accessMeta?.accessMode === "link_only";
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
            20%, 40%, 60%, 80% { transform: translateX(5px); }
          }
          .shake { animation: shake 0.5s ease-in-out; }
        `}</style>
        <div className="w-full max-w-sm px-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white/60" />
            </div>
            <h1 className="text-xl font-medium text-white mb-2">Contenido Privado</h1>
            <p className="text-white/50 text-sm">
              {linkOnly
                ? "Necesitás un link de acceso válido para continuar"
                : "Ingresá la contraseña o usá un link de acceso"}
            </p>
          </div>

          {!linkOnly && (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              <div className={`relative ${isShaking ? "shake" : ""}`}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Contraseña"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all pr-12"
                  autoFocus
                  autoComplete="off"
                  disabled={busy}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {error && <p className="text-red-400 text-sm text-center">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full py-3 bg-white text-black font-medium rounded-xl hover:bg-white/90 transition-colors disabled:opacity-60"
              >
                {busy ? "…" : "Entrar"}
              </button>
            </form>
          )}

          {linkOnly && error && (
            <p className="text-red-400 text-sm text-center mb-4">{error}</p>
          )}
          {linkOnly && !error && (
            <p className="text-white/40 text-sm text-center">
              Abrí el link completo que te compartieron.
            </p>
          )}

          <div className="mt-8 text-center">
            <a href="/" className="text-white/40 hover:text-white/60 text-sm">
              ← Volver al inicio
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ReelsFeed
      initialPlaybackIds={session.playbackIds}
      maxViews={session.maxViewsPerSession}
      emptyMessage="Todavía no hay videos en este feed"
      onReachEnd={() => {
        console.debug("User watched reels (limit or end)");
      }}
    />
  );
};

export default Reels;
